import React, { useState } from 'react';
import type { SiteDocument } from '../../../types/siteRag';
import type { SupplyChainMaterialScopeLimitation, SupplyChainPlan } from '../../../types/supplyChain';
import { materialScopeLimitationText, validateMaterialScopeLimitations } from '../../../lib/supplyChainMaterialScope';

interface Props {
  plan: SupplyChainPlan;
  documents: readonly Pick<SiteDocument, 'id' | 'fileName'>[];
  onSave: (rows: SupplyChainMaterialScopeLimitation[]) => Promise<{ ok: boolean; error?: string }>;
}
const fieldClass = 'w-full rounded-sm border border-border bg-background px-2 py-1 text-xs';

/** Normal owner editing; unresolved equipment scope is never coerced into a BOM row. */
export function MaterialScopeLimitationsEditor({ plan, documents, onSave }: Props) {
  const [editing, setEditing] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [unknownFields, setUnknownFields] = useState<SupplyChainMaterialScopeLimitation['unknownFields']>([]);
  const [sourceDocumentIds, setSourceDocumentIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const rows = validateMaterialScopeLimitations(plan.materialScopeLimitations);
  const summaries = materialScopeLimitationText(plan);
  const begin = (row?: SupplyChainMaterialScopeLimitation) => {
    setEditing(row?.id ?? 'new'); setTitle(row?.title ?? ''); setDescription(row?.description ?? '');
    setUnknownFields(row?.unknownFields ?? []); setSourceDocumentIds(row?.sourceDocumentIds ?? []); setError('');
  };
  const persist = async (next: SupplyChainMaterialScopeLimitation[]) => {
    setBusy(true); setError('');
    try {
      validateMaterialScopeLimitations(next, new Set(documents.map(doc => doc.id)));
      const result = await onSave(next);
      if (!result.ok) throw new Error(result.error ?? 'Material scope could not be saved.');
      setEditing(null);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Material scope could not be saved.'); }
    finally { setBusy(false); }
  };
  return (
    <section className="space-y-2 rounded-sm border border-border p-3" aria-label="Unpriced material scope" data-testid="scm-material-scope">
      <h3 className="text-xs font-semibold">Unpriced material scope</h3>
      <p className="text-xs text-text-muted">Record equipment whose quantity, cost or lead time is unknown. It stays excluded from the priced subtotal and is disclosed in the report; no zero-value equipment row is created.</p>
      {rows.map((row, index) => (
        <div key={row.id} className="space-y-1 border-t border-border pt-2">
          <p className="whitespace-pre-wrap break-words text-xs">{summaries[index]}</p>
          <button type="button" disabled={busy} className="mr-3 text-xs underline" onClick={() => begin(row)}>Edit {row.title}</button>
          <button type="button" disabled={busy} className="text-xs underline" onClick={() => void persist(rows.filter(candidate => candidate.id !== row.id))}>Remove {row.title}</button>
        </div>
      ))}
      {editing === null ? <button type="button" className="text-xs underline" onClick={() => begin()}>Add unpriced scope</button> : (
        <form className="space-y-2" onSubmit={event => {
          event.preventDefault();
          const row: SupplyChainMaterialScopeLimitation = {
            id: editing === 'new' ? `scope-${crypto.randomUUID()}` : editing,
            title, description, unknownFields, costTreatment: 'excluded-unpriced',
            ...(sourceDocumentIds.length ? { sourceDocumentIds } : {}),
          };
          void persist(editing === 'new' ? [...rows, row] : rows.map(old => old.id === editing ? row : old));
        }}>
          <label className="block text-xs">Scope title<input aria-label="Scope title" required maxLength={200} className={fieldClass} value={title} onChange={event => setTitle(event.target.value)} /></label>
          <label className="block text-xs">Scope description<textarea aria-label="Scope description" required maxLength={4000} className={fieldClass} value={description} onChange={event => setDescription(event.target.value)} /></label>
          <fieldset className="text-xs"><legend>Unknown fields</legend>
            {(['quantity', 'cost', 'lead-time'] as const).map(field => <label key={field} className="mr-3 inline-flex items-center gap-1"><input type="checkbox" checked={unknownFields.includes(field)} onChange={event => setUnknownFields(event.target.checked ? [...unknownFields, field] : unknownFields.filter(old => old !== field))} />{field === 'lead-time' ? 'Lead time' : field === 'cost' ? 'Cost' : 'Quantity'}</label>)}
          </fieldset>
          {documents.length > 0 && <fieldset className="text-xs"><legend>Linked source documents (optional)</legend>{documents.map(doc => <label key={doc.id} className="flex gap-1"><input type="checkbox" checked={sourceDocumentIds.includes(doc.id)} onChange={event => setSourceDocumentIds(event.target.checked ? [...sourceDocumentIds, doc.id] : sourceDocumentIds.filter(id => id !== doc.id))} />{doc.fileName}</label>)}</fieldset>}
          <button type="submit" disabled={busy} className="mr-3 text-xs underline">{busy ? 'Saving scope…' : 'Save unpriced scope'}</button>
          <button type="button" disabled={busy} className="text-xs underline" onClick={() => { setEditing(null); setError(''); }}>Cancel</button>
        </form>
      )}
      {error && <p role="alert" className="text-xs text-red-500">{error}</p>}
    </section>
  );
}
